import { useHistory, useLocation } from "react-router-dom";
import { useEffect, useState, useMemo, useRef, Fragment, useCallback } from "react";
import AdminContentLayout from "../layouts/AdminContentLayout";
import useTestOrders from "../services/utils/custom hooks/useTestOrders";
import TwoColumnLayout from "../layouts/TwoColumnLayout";
import ErrorBoundary from "../components/global/ErrorBoundary";
import SummarySectionIndicator from "../components/global/LoadingIndicators/SummarySectionIndicator";
import SidebarLoadingIndicator from "../components/global/LoadingIndicators/SidebarLoadingIndicator";
import { useOktaAuth } from "@okta/okta-react";
import toastMessages from "../services/utils/helpers/toastMessage";
import FileActions from "../services/utils/FileActions";
import Button from "../components/organism/Button";
import ApprovalTabs from "../components/atomic/ApprovalTabs";
import ReviewSection from "../components/molecular/ReviewSection";
import formatOrderData from "../components/atomic/formatOrderData";
import PDFPrint from "../components/global/PDFPrint";
import { useQueryClient } from "@tanstack/react-query";
import CustomPopUp from "../components/global/CustomPopup";
import CustomSignatureInput from "../components/global/CustomSignatureInput";
import useSignature from "../services/utils/custom hooks/eSignature/useSignature";
import useSendForApproval from "../services/utils/custom hooks/Approvals/useSendForApproval";
import ExistingSignaturePopup from "../components/popups/ExistingSignaturePopup";
import useFeatureFlags from "../services/utils/custom hooks/Feature Flagging/useFeatureFlags";
import dataConstants from "../constants/dataConstants";
import ApprovalPopup from "../components/molecular/ApprovalPopup";
import DeclineEsopredictOrderPopup from "../components/popups/DeclineEsopredictOrderPopup";
import SignatureOrderTexts from "../services/utils/helpers/signatureOrderTexts";
import { useTestOrdersByIds } from "../services/utils/custom hooks/useTestOrdersById";
import formatApprovalDesktopData from "../components/atomic/formatApprovalDesktopData";
import getOrderTestDisplayLabel from "../utils/helpers/getOrderTestDisplayLabel";

function ApprovalsReview() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const { isSectionActive, getActionsForSection } = useFeatureFlags();
  const ids = searchParams.get("ids")?.split(",");
  const history = useHistory();
  // const results = useTestOrders(ids);
  const {
    data: resultsByNewApi,
    isLoading: loadingOrderByIds,
    isError: errorOrderOfIds,
  } = useTestOrdersByIds({
    ids: ids ?? [],
    skipPagination: true,
  });
  // const loading = results?.some((result) => result?.isLoading);
  // const error = results?.some((result) => result?.isError);
  // const data = useMemo(() => results?.map((result) => result?.data), [results]);
  const newData: any = useMemo(
    () => resultsByNewApi?.data?.orders?.map((result) => result),
    [resultsByNewApi]
  );
  const fileActions = new FileActions();
  const { authState } = useOktaAuth();
  const [downloadingReport, setDownloadingReport] = useState<boolean>(false);
  const [orders, setOrders] = useState([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  // const testIds = data.map((e: any) => e?.data?.testTypes?.id);
  const testIdsNew = newData?.map((e: any) => e?.testTypes?.id);
  // const hasIdEight = testIds.includes(8);
  const hasIdEightNew = testIdsNew?.includes(8);
  const queryClient = useQueryClient();
  const patientNames = useMemo(
    () => newData?.map((order: any) => order?.patientFirstName),
    [newData]
  );
  const testName = useMemo(
    () =>
      newData?.map((order: any) =>
        getOrderTestDisplayLabel(order, { useFullName: false })
      ),
    [newData]
  );

  // Format header title to prevent overflow
  const formattedSubHeaderTitle = useMemo(() => {
    if (loadingOrderByIds && newData?.length > 1) {
      return `Approvals | CSTL`;
    }

    if (!patientNames || !testName || patientNames.length === 0 || !newData) {
      return `Approval | CSTL`;
    }

    const totalOrdersCount = newData.length;

    // Get unique patient names and test names (for display)
    const uniquePatientNames = Array.from(new Set(patientNames.filter(Boolean)));
    const uniqueTestNames = Array.from(new Set(testName.filter(Boolean)));

    // Format: Show up to 2 names for each, then show remaining order count only once at the end
    const patientText = uniquePatientNames.slice(0, 2).join(", ");
    const testText = uniqueTestNames.slice(0, 2).join(", ");

    if (totalOrdersCount <= 2) {
      return `${patientText} - ${testText}`;
    }

    return `${patientText} - ${testText} +${totalOrdersCount - 2} more`;
  }, [loadingOrderByIds, newData, patientNames, testName]);
  const profile = queryClient.getQueryData<any>(["profile"]);
  const roles = profile?.user?.roles;
  const [selectedTabs, setSelectedTabs] = useState<any>({});
  const [multiPrinting, setMultiPrinting] = useState<boolean>(false);
  const handleTabClick = (orderId: any, tabId: any) => {
    setSelectedTabs((prev: any) => ({ [orderId]: tabId }));
    const element = document.getElementById(`${orderId}-${tabId}`);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
      });
    }
  };
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [showSignaturePopup, setShowSignaturePopup] = useState<boolean>(false);
  const [showPreviewPopup, setShowPreviewPopup] = useState<boolean>(false);
  const { isLoading: fetchingSignature, data: signatureData } = useSignature();
  const [showDisclaimer, setShowDisclaimer] = useState<boolean>(false);
  const [signatureURL, setSignatureURL] = useState<string>("");
  const [noteVisible, setNoteVisible] = useState<boolean>(false);
  const [showDeclinePopup, setShowDeclinePopup] = useState<boolean>(false);
  const { mutate, isPending } = useSendForApproval();
  const canAccess =
    isSectionActive(dataConstants.FeatureFlagSectionKeys.APPROVALS) &&
    getActionsForSection(dataConstants.FeatureFlagSectionKeys.APPROVALS).find(
      (action) => action.key === dataConstants.SectionActionKeys.AUTHORIZE_ORDER
    )?.isActive;
  const has6thTestTypeOrder = orders.some(
    (order: any) => order.testType.test === 8
  );

  const handleAuthorization = () => {
    const ordersBelongsToLoggedInPhysician = orders.every(
      (order: any) =>
        order?.clinicianInfoPhysician?.email === profile?.user?.email
    );

    const isPhysician = roles?.includes("Physician");

    if (isPhysician && ordersBelongsToLoggedInPhysician) {
      if (!!signatureData === false) {
        setShowSignaturePopup(true);
      } else {
        setShowPreviewPopup(true);
      }
    } else if (roles?.includes("Office Manager")) {
      setNoteVisible(true);
    } else setIsPopupOpen(true);
  };
  const [selectedIds, setSelectedIds] = useState<any>(
    newData?.map((e: any) => e?.id)
  );
  const selectedOrders: any[] = orders.filter((order: any) => {
    const found = selectedIds.find((orderId: number) => orderId == order.id);

    if (found) return true;
    return false;
  });
  const hideManualSubmission =
    selectedOrders
      ?.map((order: any) => order?.orderStatus === "COA Order Attestation")
      ?.includes(true) && roles?.includes("Office Manager");
  const combinedLabels = useMemo(
    () =>
      newData
        ?.filter((order: any) => selectedIds?.includes(order?.id))
        .map((order: any) => ({
          patientName: order?.patientFirstName,
          testName: order?.testTypes?.limsTestName,
        })),
    [newData, selectedIds]
  );

  const pendingOrderAuthOrders = useMemo(
    () =>
      newData?.filter(
        (o: any) =>
          selectedIds?.includes(o?.id) &&
          o?.orderStatus === "Pending Order Authorization"
      ) ?? [],
    [newData, selectedIds]
  );
  const handleCheckboxChange = (id: string) => {
    setSelectedIds((prevSelectedIds: any) =>
      prevSelectedIds.includes(id)
        ? prevSelectedIds.filter((selectedId: any) => selectedId !== id)
        : [...prevSelectedIds, id]
    );
  };

  function closePopup() {
    setIsPopupOpen(false);
  }

  const handlePrintStart = useCallback(() => {
    setMultiPrinting(true);
  }, []);

  const handlePrintError = useCallback((message: string) => {
    toastMessages.error(message);
    setMultiPrinting(false);
  }, []);

  const handlePrintSuccess = useCallback(() => {
    setMultiPrinting(false);
    // Optional: clear IDs after a tiny delay if needed
    // setSelectedIds([]); 
  }, []);
  function printOrder(): void {
    if (selectedIds && selectedIds.length > 0) {
      setMultiPrinting(true);
    } else {
      toastMessages.error("No orders selected to print");
    }
  }

  async function handleOrderDownload() {
    if (!selectedIds || selectedIds.length === 0) {
      toastMessages.error("No orders selected to download");
      return;
    }
    setDownloadingReport(true);
    try {
      const payload: any = newData
        ?.filter((e: any) => selectedIds?.includes(e?.id))
        .map((e: any) => {
          let testName = !!e?.testTypes?.name
            ? e?.testTypes?.name.split(":")[0]
            : "";
          let fileName = `${e.id}_${!!e.patientFirstName ? `${e.patientFirstName} ` : "Patient"
            }_${testName}`;

          return {
            id: e.id,
            fileName: fileName,
          };
        });
      await fileActions.downloadMultiplePDF(
        payload,
        authState?.accessToken?.accessToken ?? ""
      );
    } catch (error: any) {
      toastMessages.error(
        error?.response?.data?.message ??
        error.message ??
        "Something went wrong"
      );
    } finally {
      setDownloadingReport(false);
    }
  }
  useEffect(() => {
    if (canAccess) {
      if (!loadingOrderByIds && newData && newData?.length > 0) {
        setOrders(formatApprovalDesktopData(newData));
      }
      setSelectedIds(newData?.map((e: any) => e?.id));
    } else if (!canAccess && roles?.length > 0) {
      history.push("/unauthorized");
    }
  }, [loadingOrderByIds]);

  return (
    <Fragment>
      <AdminContentLayout
        pageTitle={
          loadingOrderByIds && newData?.length > 1
            ? `Approvals | CSTL`
            : `Approval | CSTL`
        }
        subHeaderTitle={formattedSubHeaderTitle}
        redirect={() => history.goBack()}
        loading={loadingOrderByIds}
        rightSectionClassName="hidden md:block"
        customButtons={[
          {
            buttonTheme: "secondary",
            textInformation: {
              text: "PRINT",
              fontWeight: "font-semibold",
            },
            onClick: () => printOrder(),
            loading: multiPrinting,
            type: "submit",
            className: "mr-6",
            disabled: multiPrinting || loadingOrderByIds,
          },
          {
            buttonTheme: "secondary",
            textInformation: {
              text: `${orders.length > 1 ? "DOWNLOAD ORDERS" : "DOWNLOAD ORDER"
                }`,
              fontWeight: "font-semibold",
            },
            onClick: () => handleOrderDownload(),
            type: "submit",
            loading: downloadingReport,
            disabled: downloadingReport || loadingOrderByIds,
            className: "mr-6",
          },
          ...(pendingOrderAuthOrders.length > 0
            ? [
                {
                  buttonTheme: "secondary" as const,
                  textInformation: {
                    text:
                      pendingOrderAuthOrders.length > 1
                        ? `DECLINE (${pendingOrderAuthOrders.length})`
                        : "DECLINE ORDER",
                    fontWeight: "font-semibold",
                  },
                  onClick: () => setShowDeclinePopup(true),
                  type: "submit" as const,
                  disabled: loadingOrderByIds,
                  className: "mr-6",
                },
              ]
            : []),
          {
            buttonTheme: "primary",
            textInformation: {
              text: `${orders.length > 1
                ? `AUTHORIZE ALL (${selectedIds?.length})`
                : "AUTHORIZE"
                }`,
              fontWeight: "font-semibold",
            },
            onClick: handleAuthorization,
            loading: isPending,
            type: "submit",
            className: "mr-6",
            disabled:
              selectedIds?.length === 0 ||
              loadingOrderByIds ||
              isPending ||
              fetchingSignature,
          },
        ]}
      >
        <TwoColumnLayout>
          <ErrorBoundary fallbackClassName="h-[80vh]">
            <div className="block bg-sideNavGray border-r md:h-[calc(100vh_-_178px)] lg:h-[calc(100vh_-_120px)] min-w-0 overflow-auto">
              {loadingOrderByIds ? (
                <SidebarLoadingIndicator />
              ) : (
                newData?.map((order: any, index: any) => (
                  <div key={order?.id} className="stepContainerClassName">
                    <ApprovalTabs
                      orderId={order?.id}
                      handleTabClick={handleTabClick}
                      selectedTab={selectedTabs[order?.id]}
                      testTypeId={order?.testTypes?.id}
                      orders={orders}
                      index={index}
                      selectedIds={selectedIds}
                      handleCheckboxChange={handleCheckboxChange}
                    />
                  </div>
                ))
              )}
            </div>
            {loadingOrderByIds ? (
              <SummarySectionIndicator />
            ) : (
              <div className="overflow-auto flex flex-col py-5 px-6 lg:px-32 md:h-[calc(100vh_-_178px)] lg:h-[calc(100vh_-_120px)]">
                {orders.map((order: any, index) => {
                  const isChecked = selectedIds.includes(
                    order?.supportingSection?.id
                  );
                  return (
                    <ReviewSection
                      key={index}
                      index={index}
                      order={order}
                      isChecked={isChecked}
                      handleCheckboxChange={handleCheckboxChange}
                    />
                  );
                })}
              </div>
            )}
          </ErrorBoundary>
        </TwoColumnLayout>
      </AdminContentLayout>

      <PDFPrint
        onStart={handlePrintStart}
        onError={handlePrintError}
        onSuccess={handlePrintSuccess}
        ids={multiPrinting ? selectedIds.map((id: any) => String(id)) : []}
      />
      {isPopupOpen && (
        <div className="fixed inset-0 z-[2000000] flex items-center justify-center bg-modalBackdrop overflow-hidden">
          <ApprovalPopup
            ids={selectedIds}
            closePopup={closePopup}
            submitting={submitting}
            downloadOrders={handleOrderDownload}
            hasIdg={hasIdEightNew}
            downloading={downloadingReport}
            hideManualSubmission={hideManualSubmission}
            labels={combinedLabels}
          />
        </div>
      )}
      {showSignaturePopup && (
        <CustomPopUp
          popUpTitle="E-Signature"
          popUpSubtitle="E-signature is required for order authorization"
          hidePopUp={() => setShowSignaturePopup(false)}
        >
          <SignatureOrderTexts
            data={selectedOrders}
            roles={roles}
            className=""
          />
          <CustomSignatureInput
            submitting={isPending}
            handleSubmission={(signatureUrl) => {
              if (has6thTestTypeOrder) {
                setSignatureURL(signatureUrl);
                setShowSignaturePopup(false);
                setShowDisclaimer(true);

                return;
              }

              mutate(
                {
                  username: "",
                  password: "",
                  orderIds: selectedIds,
                  file: {
                    fileName: `${profile?.user?.id}_${profile?.user?.firstName}_signature_file.png`,
                    base64url: signatureUrl,
                  },
                },
                {
                  onSuccess: () => {
                    history.push("/access/approvals/order-authorized");
                  },
                }
              );
            }}
          />
        </CustomPopUp>
      )}
      {showPreviewPopup && (
        <CustomPopUp
          popUpTitle="E-Signature"
          popUpSubtitle=" This signature will be used for order authorizations"
          subTitleClassName="before:content-['Note:'] before:text-requiredRed before:font-semibold"
          hidePopUp={() => setShowPreviewPopup(false)}
        >
          <SignatureOrderTexts
            data={selectedOrders}
            roles={roles}
            className=""
          />
          <ExistingSignaturePopup
            imageURL={signatureData?.viewableURL}
            // imageURL="https://plus.unsplash.com/premium_photo-1661482843558-f16df35e432c?q=80&w=1954&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
            handleChange={() => {
              setShowPreviewPopup(false);
              setShowSignaturePopup(true);
            }}
            buttonTitle={has6thTestTypeOrder ? "Next" : "Done"}
            submitting={isPending}
            handleDone={() => {
              if (has6thTestTypeOrder) {
                setShowPreviewPopup(false);
                setShowDisclaimer(true);
                return;
              }

              mutate(
                {
                  username: "",
                  password: "",
                  orderIds: selectedIds,
                  eSignatureId: signatureData?.id,
                },
                {
                  onSuccess: () => {
                    setShowPreviewPopup(false);
                    history.push("/access/approvals/order-authorized");
                  },
                }
              );
            }}
          />
        </CustomPopUp>
      )}
      {showDisclaimer && (
        <CustomPopUp
          popUpTitle=""
          popUpSubtitle=" "
          hidePopUp={() => setShowDisclaimer(false)}
        >
          <div className="border border-roundedInputBorder p-4 rounded-md bg-sideNavGray my-3 max-w-[50vw]">
            <h3 className="font-semibold !text-sm text-royalBlue">
              Disclaimer
            </h3>
            <p className="text-royalBlue text-xs mt-5">
              By my signature below, I, the healthcare provider, authorize
              performance of NeuroIDgenetix 902 and indicate that the purpose of
              the test(s), the procedures, the benefits and risks involved have
              been explained to the patient and patient informed consent has
              been obtained in accordance with state and local laws. I attest
              that this patient has a diagnosis for which pharmacologic therapy
              is reasonable and necessary, and that the drug or drugs that I
              intend to prescribe are reasonable and necessary for the treatment
              of this patient's diagnosis. I have made an initial personalized
              decision for this patient based on the patient's non-genetic
              factors. I have a record of what drug(s) is/are being considered
              and for what indication(s) to ensure the test(s) performed is
              reasonable and necessary. The test results will be utilized by me
              to determine a dose change to the patient's current medication
              and/or prescribe a new medication. I have informed the patient
              that Castle Biosciences, Inc. may be an out-of-network provider
              under their insurance plan and that I do not have any financial
              interest in Castle Biosciences, Inc.
            </p>
          </div>
          <Button
            buttonTheme="primary"
            disabled={isPending}
            loading={isPending}
            textInformation={{
              text: "Agree & Submit",
              fontWeight: "font-semibold",
            }}
            onClick={() => {
              const navOnSuccess = () => {
                history.push("/access/approvals/order-authorized");
              };
              if (!!signatureURL) {
                mutate(
                  {
                    username: "",
                    password: "",
                    orderIds: selectedIds,
                    file: {
                      fileName: `${profile?.user?.id}_${profile?.user?.firstName}_signature_file.png`,
                      base64url: !!signatureURL ? signatureURL : "",
                    },
                  },
                  { onSuccess: navOnSuccess }
                );
              }
              mutate(
                {
                  username: "",
                  password: "",
                  orderIds: selectedIds,
                  eSignatureId: !!signatureData?.id ? signatureData.id : "",
                },
                { onSuccess: navOnSuccess }
              );
            }}
          />
        </CustomPopUp>
      )}
      {showDeclinePopup && (
        <DeclineEsopredictOrderPopup
          orders={pendingOrderAuthOrders.map((o: any) => ({
            id: o.id,
            patientFirstName: o.patientFirstName,
            patientLastName: o.patientLastName,
          }))}
          onClose={() => setShowDeclinePopup(false)}
          onDeclineSuccess={() => {
            setShowDeclinePopup(false);
            history.goBack();
          }}
        />
      )}
      {noteVisible && (
        <CustomPopUp
          popUpTitle="E-Signature"
          popUpSubtitle=" Are you sure you want to authorize the following orders with physician credentials?"
          subTitleClassName="before:content-['Note:'] before:text-requiredRed before:font-semibold"
          hidePopUp={() => setNoteVisible(false)}
        >
          <SignatureOrderTexts
            data={selectedOrders}
            roles={roles}
            className="!w-full"
          />
          <div className="flex w-full justify-end items-center">
            <Button
              buttonTheme="secondary"
              disabled={false}
              loading={false}
              className="mx-3"
              textInformation={{
                text: "No",
                fontWeight: "font-semibold",
              }}
              onClick={() => {
                setNoteVisible(false);
              }}
            />
            <Button
              buttonTheme="primary"
              disabled={false}
              loading={false}
              textInformation={{
                text: "Yes",
                fontWeight: "font-semibold",
              }}
              onClick={() => {
                setNoteVisible(false);
                setIsPopupOpen(true);
              }}
            />
          </div>
        </CustomPopUp>
      )}
    </Fragment>
  );
}

export default ApprovalsReview;
